import { useEffect, useState, useCallback, useMemo } from "react"
import { useSearchParams } from "react-router"
import { useTranslation } from "react-i18next"

import {
  FetchStatus,
  useGetOrganizationUsersApi,
  useGetPendingOrganizationInvitationsApi,
  useRemoveOrganizationUsersApi,
  useGetOrganizationLimitUsageApi,
  useRevokePendingOrganizationInvitationApi,
  useResendPendingOrganizationInvitationApi,
  pendingOrganizationInvitationResponseToInvitation,
  transformOrganizationLimitUsage,
  userRoleResponseToMember,
  RemoveOrganizationUsersRequest,
  OrganizationLimitUsageItem,
} from "@/hooks/api"
import { usePermissionCheck, useToast } from "@/hooks"
import { PermissionButton } from "@/components/Auth"
import { Member, OrganizationRole, PageInfo, Permission } from "@/types"
import { pageInfoFromPagination } from "@/types/converter"
import { DEFAULT_PAGE_SIZE } from "@/constants"
import { SOMETHING_WRONG_MSG } from "@/constants/errors"
import { useStore } from "@/store"
import { ErrorPage } from "@/components/System"
import { getUserFacingApiErrorMessage } from "@/lib/apiErrors"

import {
  OrganizationUserTable,
  MemberDialog,
  PendingInvitationsSection,
} from "../components"

interface UserUsageStripProps {
  usage?: OrganizationLimitUsageItem | null
}

const formatCount = (value: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value)

const UserUsageStrip = ({ usage }: UserUsageStripProps) => {
  const { t } = useTranslation()
  const used = usage?.used ?? 0
  const limit = usage?.limit ?? null
  const activeCount = usage?.details?.active_count ?? used
  const pendingInvitationCount = usage?.details?.pending_invitation_count ?? 0
  const atLimit = Boolean(usage?.atLimit)
  const percent =
    limit == null ? 0 : limit <= 0 ? 100 : Math.min(100, (used / limit) * 100)
  const barColor =
    atLimit || percent >= 100
      ? "bg-red-500"
      : percent >= 80
        ? "bg-yellow-500"
        : "bg-green-500"
  const textColor =
    atLimit || percent >= 100
      ? "text-red-700 dark:text-red-400"
      : percent >= 80
        ? "text-amber-700 dark:text-amber-400"
        : "text-slate-600 dark:text-slate-300"
  const limitLabel =
    limit == null ? t("settings.users.usage.unlimited") : formatCount(limit)
  const remaining = limit == null ? null : Math.max(0, limit - used)

  return (
    <div className="rounded-md border border-slate-200 bg-slate-50 px-4 py-3 dark:border-slate-800 dark:bg-slate-900/40">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-1">
          <div className="text-sm font-medium text-slate-900 dark:text-slate-50">
            {t("settings.users.usage.title")}
          </div>
          <div className="text-xs text-slate-500 dark:text-slate-400">
            {t("settings.users.usage.breakdown", {
              active: formatCount(activeCount),
              pending: formatCount(pendingInvitationCount),
            })}
          </div>
          <div className={`text-xs ${textColor}`}>
            {atLimit
              ? t("settings.users.usage.atLimit")
              : remaining == null
                ? t("settings.users.usage.unlimitedDescription")
                : t("settings.users.usage.remaining", {
                    remaining: formatCount(remaining),
                  })}
          </div>
        </div>
        <div className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {t("settings.users.usage.value", {
            used: formatCount(used),
            limit: limitLabel,
          })}
        </div>
      </div>
      {limit != null && (
        <div className="mt-3 h-2 w-full rounded-full bg-slate-200 dark:bg-slate-800">
          <div
            className={`${barColor} h-2 rounded-full transition-all`}
            style={{ width: `${percent}%` }}
          />
        </div>
      )}
    </div>
  )
}

const OrganizationUsersTab = () => {
  const { alertToast, successToast } = useToast()
  const [searchParams, setSearchParams] = useSearchParams()
  const [currentOrganization] = useStore((state) => [state.currentOrganization])
  const [showMemberDialog, setShowMemberDialog] = useState(false)
  const [members, setMembers] = useState<Member[]>([])
  const [pendingInvitations, setPendingInvitations] = useState<
    ReturnType<typeof pendingOrganizationInvitationResponseToInvitation>[]
  >([])
  const [pageInfo, setPageInfo] = useState<PageInfo>({
    limit: DEFAULT_PAGE_SIZE,
    offset: 0,
    totalRecords: 0,
    totalPages: 0,
    current: 1,
  })
  const { t } = useTranslation()
  const { hasPermission: canManageInvitations } = usePermissionCheck({
    permission: Permission.OrgAddMembers,
  })

  // APIs
  const { data: organizationUsersResp, fetchFn: getOrganizationUsers } =
    useGetOrganizationUsersApi(currentOrganization!.id)
  const { fetchFn: getPendingInvitations } =
    useGetPendingOrganizationInvitationsApi(currentOrganization!.id)
  const { data: limitUsageResp, fetchFn: getLimitUsage } =
    useGetOrganizationLimitUsageApi(currentOrganization!.id)
  const {
    data: removeOrganizationUsersResp,
    error: removeOrganizationUsersError,
    fetchFn: removeOrganizationUsers,
  } = useRemoveOrganizationUsersApi(currentOrganization!.id)
  const {
    data: revokePendingInvitationResp,
    error: revokePendingInvitationError,
    fetchFn: revokePendingInvitation,
    status: revokePendingInvitationStatus,
  } = useRevokePendingOrganizationInvitationApi(currentOrganization!.id)
  const {
    data: resendPendingInvitationResp,
    error: resendPendingInvitationError,
    fetchFn: resendPendingInvitation,
    status: resendPendingInvitationStatus,
  } = useResendPendingOrganizationInvitationApi(currentOrganization!.id)

  const refreshOrganizationUsers = useCallback(() => {
    const page = parseInt(searchParams.get("page") || "1")
    getOrganizationUsers({
      searchParams: new URLSearchParams({
        limit: DEFAULT_PAGE_SIZE.toString(),
        offset: ((page - 1) * DEFAULT_PAGE_SIZE).toString(),
      }),
    })
  }, [getOrganizationUsers, searchParams])

  const refreshPendingInvitations = useCallback(() => {
    if (!canManageInvitations) {
      setPendingInvitations([])
      return
    }

    getPendingInvitations({
      callbackFn: (resp) => {
        setPendingInvitations(
          (resp?.data || []).map(
            pendingOrganizationInvitationResponseToInvitation
          )
        )
      },
    }).catch((error) => {
      alertToast({
        title: t("settings.users.invitation.pending.loadFailedTitle"),
        description: getUserFacingApiErrorMessage(
          error,
          t,
          t("settings.users.invitation.pending.loadFailedDescription")
        ),
      })
    })
  }, [alertToast, canManageInvitations, getPendingInvitations, t])

  const refreshLimitUsage = useCallback(() => {
    getLimitUsage({ preserveData: true }).catch((error) => {
      alertToast({
        title: t("settings.users.usage.errors.loadFailedTitle"),
        description: getUserFacingApiErrorMessage(
          error,
          t,
          t("settings.users.usage.errors.loadFailedDescription")
        ),
      })
    })
  }, [alertToast, getLimitUsage, t])

  const updateSearchParams = (page?: number) => {
    const params = new URLSearchParams(searchParams.toString())
    if (page) {
      params.set("page", page.toString())
    }
    setSearchParams(params)
  }

  useEffect(() => {
    refreshOrganizationUsers()
    refreshPendingInvitations()
    refreshLimitUsage()
  }, [refreshOrganizationUsers, refreshPendingInvitations, refreshLimitUsage])

  useEffect(() => {
    if (!organizationUsersResp || !organizationUsersResp.data) return
    setMembers(organizationUsersResp.data.map(userRoleResponseToMember))
    if (organizationUsersResp.pagination) {
      setPageInfo(pageInfoFromPagination(organizationUsersResp.pagination))
    }
  }, [organizationUsersResp])

  useEffect(() => {
    if (!removeOrganizationUsersError) return
    console.error(
      "Failed to remove organization users",
      removeOrganizationUsersError
    )

    alertToast({
      title: t("settings.users.errors.removeFailed.title"),
      description: getUserFacingApiErrorMessage(
        removeOrganizationUsersError,
        t,
        removeOrganizationUsersResp?.message ||
          t("settings.users.errors.removeFailed.description")
      ),
    })
  }, [removeOrganizationUsersError, removeOrganizationUsersResp, alertToast, t])

  useEffect(() => {
    if (
      revokePendingInvitationStatus !== FetchStatus.Success ||
      !revokePendingInvitationResp
    ) {
      return
    }

    successToast({
      title: t("settings.users.invitation.pending.revokeSuccessTitle"),
      description: t(
        "settings.users.invitation.pending.revokeSuccessDescription"
      ),
    })
    refreshPendingInvitations()
    refreshLimitUsage()
  }, [
    refreshPendingInvitations,
    refreshLimitUsage,
    revokePendingInvitationResp,
    revokePendingInvitationStatus,
    successToast,
    t,
  ])

  useEffect(() => {
    if (
      revokePendingInvitationStatus !== FetchStatus.Error ||
      !revokePendingInvitationError
    ) {
      return
    }

    alertToast({
      title: t("settings.users.invitation.pending.revokeFailedTitle"),
      description: getUserFacingApiErrorMessage(
        revokePendingInvitationError,
        t,
        t("settings.users.invitation.pending.revokeFailedDescription")
      ),
    })
  }, [
    alertToast,
    revokePendingInvitationError,
    revokePendingInvitationStatus,
    t,
  ])

  useEffect(() => {
    if (
      resendPendingInvitationStatus !== FetchStatus.Success ||
      !resendPendingInvitationResp
    ) {
      return
    }

    successToast({
      title: t("settings.users.invitation.pending.resendSuccessTitle"),
      description: t(
        "settings.users.invitation.pending.resendSuccessDescription"
      ),
    })
    refreshPendingInvitations()
    refreshLimitUsage()
  }, [
    refreshPendingInvitations,
    refreshLimitUsage,
    resendPendingInvitationResp,
    resendPendingInvitationStatus,
    successToast,
    t,
  ])

  useEffect(() => {
    if (
      resendPendingInvitationStatus !== FetchStatus.Error ||
      !resendPendingInvitationError
    ) {
      return
    }

    alertToast({
      title: t("settings.users.invitation.pending.resendFailedTitle"),
      description: getUserFacingApiErrorMessage(
        resendPendingInvitationError,
        t,
        t("settings.users.invitation.pending.resendFailedDescription")
      ),
    })
  }, [
    alertToast,
    resendPendingInvitationError,
    resendPendingInvitationStatus,
    t,
  ])

  const handleInviteSuccess = useCallback(() => {
    refreshOrganizationUsers()
    refreshPendingInvitations()
    refreshLimitUsage()
  }, [refreshOrganizationUsers, refreshPendingInvitations, refreshLimitUsage])

  const handleRevokeInvitation = useCallback(
    (invitationId: string) => {
      revokePendingInvitation({
        urlBuilderParams: {
          organizationId: currentOrganization!.id,
          invitationId,
        },
      }).catch(() => undefined)
    },
    [currentOrganization, revokePendingInvitation]
  )

  const handleResendInvitation = useCallback(
    (invitationId: string) => {
      resendPendingInvitation({
        urlBuilderParams: {
          organizationId: currentOrganization!.id,
          invitationId,
        },
      }).catch(() => undefined)
    },
    [currentOrganization, resendPendingInvitation]
  )

  const handlePageChange = (page: number) => {
    updateSearchParams(page)
  }

  const handleRoleChange = (member: Member, role: OrganizationRole) => {
    member.role = role
    successToast({
      title: t("settings.users.errors.roleChanged.title"),
      description: t("settings.users.errors.roleChanged.description", {
        name: member.name,
        role: role.charAt(0).toUpperCase() + role.slice(1),
      }),
    })
    refreshOrganizationUsers()
  }

  const handleRemoveUser = (member: Member) => {
    const reqData: RemoveOrganizationUsersRequest = {
      user_ids: [member.id],
    }
    removeOrganizationUsers({
      jsonBody: reqData,
      callbackFn: (resp) => {
        if (!resp || !resp.data) return
        successToast({
          title: t("settings.users.errors.userRemoved.title"),
          description: t("settings.users.errors.userRemoved.description"),
        })
        refreshOrganizationUsers()
        refreshLimitUsage()
      },
    })
  }

  const handleError = useCallback(
    (error: Error) => {
      alertToast({
        title: t(SOMETHING_WRONG_MSG),
        description: error.message,
      })
    },
    [alertToast, t]
  )

  const userUsage = useMemo(() => {
    if (!limitUsageResp?.data) return null
    return (
      transformOrganizationLimitUsage(limitUsageResp.data).items.find(
        (item) => item.key === "users"
      ) ?? null
    )
  }, [limitUsageResp?.data])
  const inviteDisabledByUsage = Boolean(userUsage?.atLimit)

  if (!currentOrganization) {
    return <ErrorPage message={t("settings.users.errors.orgNotFound")} />
  }

  return (
    <>
      <div className="flex flex-col">
        <h3 className="text-2xl font-semibold leading-none tracking-tight">
          {t("settings.users.title")}
        </h3>
        <p className="mt-2 mb-4 text-sm text-slate-500 dark:text-slate-400">
          {t("settings.users.description")}
        </p>
        <div className="my-4 flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="sm:max-w-md sm:flex-1">
            {userUsage ? <UserUsageStrip usage={userUsage} /> : null}
          </div>
          <PermissionButton
            permission={Permission.OrgAddMembers}
            disabledTooltip={t("common.noPermission")}
            disabled={inviteDisabledByUsage}
            size="sm"
            onClick={() => setShowMemberDialog(true)}
          >
            {t("settings.users.inviteUser")}
          </PermissionButton>
        </div>
        <OrganizationUserTable
          organization={currentOrganization}
          members={members}
          pageInfo={pageInfo}
          onPageChange={handlePageChange}
          onRoleChange={handleRoleChange}
          onRemove={handleRemoveUser}
          onError={handleError}
        />
        {canManageInvitations && (
          <PendingInvitationsSection
            invitations={pendingInvitations}
            isRevoking={revokePendingInvitationStatus === FetchStatus.Loading}
            isResending={resendPendingInvitationStatus === FetchStatus.Loading}
            onRevoke={handleRevokeInvitation}
            onResend={handleResendInvitation}
          />
        )}
      </div>

      {/* Dialogs */}
      <MemberDialog
        currentMembers={members}
        open={showMemberDialog}
        onOpenChange={setShowMemberDialog}
        onSuccess={handleInviteSuccess}
      />
    </>
  )
}

export default OrganizationUsersTab
